import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangleIcon, BanIcon, RefreshCwIcon, RotateCcwIcon, ShieldCheckIcon, Trash2Icon, UnlockIcon } from 'lucide-react';
import { AdminUserRow, blockUser, deleteOrganization, fetchUsersOverview, resetOrganization, unblockUser } from '../../services/adminService';
import { ensureDefaultContent } from '../../services/repair';
import { refresh as refreshStore } from '../../services/store';
import { formatDate } from '../../utils/format';
import { SYSTEM_VERSION } from '../../config/app';
import { AVA_TEMPLATE_KEY, AVA_TEMPLATE_VERSION } from '../../data/landingTemplates';
import { ConfirmDialog } from '../crm/ConfirmDialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Table } from '../ui/Table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import { Tag } from '../ui/Tag';
import { toast } from '../ui/Toast';

/** Resumen de la última sincronización masiva de plantillas. */
interface SyncSummary {
  total: number;
  updated: number;
  failed: number;
}

/**
 * Panel reservado al super administrador de la plataforma. Solo se monta
 * cuando `useAuth().isSuperAdmin` es true; además, cada RPC vuelve a validar
 * el permiso en la base de datos.
 */
export function AdminSection() {
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Diálogos de confirmación.
  const [resetTarget, setResetTarget] = useState<AdminUserRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUserRow | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [syncOpen, setSyncOpen] = useState(false);

  // Actualización masiva de plantillas.
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({
    done: 0,
    total: 0
  });
  const [syncSummary, setSyncSummary] = useState<SyncSummary | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchUsersOverview());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  /** Vacía la organización y vuelve a sembrar formulario y landings. */
  async function handleReset(row: AdminUserRow) {
    setBusyId(row.organization_id);
    try {
      await resetOrganization(row.organization_id);
      await ensureDefaultContent(row.organization_id, row.organization_name ?? 'Mi empresa');
      await refreshStore();
      toast.success('Organización reiniciada', {
        description: `${row.organization_name ?? 'La organización'} vuelve a tener su formulario y sus landings por defecto.`
      });
      await load();
    } catch (err) {
      toast.error('No se ha podido reiniciar', {
        description: err instanceof Error ? err.message : 'Error desconocido.'
      });
    } finally {
      setBusyId(null);
      setResetTarget(null);
    }
  }

  /** Bloquea o reactiva el acceso del usuario. */
  async function handleToggleBlock(row: AdminUserRow) {
    setBusyId(row.user_id);
    try {
      if (row.is_blocked) {
        await unblockUser(row.user_id);
        toast.success('Acceso reactivado');
      } else {
        await blockUser(row.user_id);
        toast.success('Acceso bloqueado');
      }
      await load();
    } catch (err) {
      toast.error('No se ha podido cambiar el acceso', {
        description: err instanceof Error ? err.message : 'Error desconocido.'
      });
    } finally {
      setBusyId(null);
    }
  }

  /** Elimina la organización completa; el usuario solo si no tiene otra. */
  async function handleDelete(row: AdminUserRow) {
    setBusyId(row.organization_id);
    try {
      const result = await deleteOrganization(row.organization_id);
      toast.success('Organización eliminada', {
        description: `${result.deleted_users ?? 0} usuario(s) eliminados · ${result.kept_users ?? 0} conservados por pertenecer a otra organización.`
      });
      await load();
    } catch (err) {
      toast.error('No se ha podido eliminar', {
        description: err instanceof Error ? err.message : 'Error desconocido.'
      });
    } finally {
      setBusyId(null);
      setDeleteTarget(null);
      setDeleteConfirmText('');
    }
  }

  /**
   * Recorre todas las organizaciones y completa su contenido con la plantilla
   * oficial. `ensureDefaultContent` es idempotente: nunca sobrescribe nada.
   */
  async function handleSyncAll() {
    setSyncOpen(false);
    setSyncing(true);
    setSyncSummary(null);
    let updated = 0;
    let failed = 0;
    const targets = rows.filter((row, index) => rows.findIndex(r => r.organization_id === row.organization_id) === index);
    setSyncProgress({
      done: 0,
      total: targets.length
    });
    for (const [index, row] of targets.entries()) {
      try {
        const result = await ensureDefaultContent(row.organization_id, row.organization_name ?? 'Mi empresa');
        if (result.created || result.addedQuestions > 0) updated += 1;
      } catch (err) {
        failed += 1;
        console.error(`[AVA] Actualización fallida en ${row.organization_id}:`, err);
      }
      setSyncProgress({
        done: index + 1,
        total: targets.length
      });
    }
    await refreshStore();
    setSyncing(false);
    setSyncSummary({
      total: targets.length,
      updated,
      failed
    });
    if (failed === 0) toast.success('Sistema actualizado', {
      description: `${targets.length} organizaciones revisadas · ${updated} actualizadas.`
    });else toast.error('Actualización con incidencias', {
      description: `${failed} organizaciones no se han podido actualizar.`
    });
  }
  const columns = [{
    key: 'name',
    header: 'Usuario',
    render: (row: AdminUserRow) => <div className="min-w-0" data-id="element-969" data-mp="3kxz8m710fz4k">
          <p className="truncate font-medium text-foreground" data-id="element-970" data-mp="3kz0iw9a7chl8">
            {row.full_name || 'Sin nombre'}
          </p>
          <p className="truncate text-xs text-muted-foreground" data-id="element-971" data-mp="3g1ikhctt9krh">
            {row.email || '—'}
          </p>
        </div>
  }, {
    key: 'organization',
    header: 'Organización',
    render: (row: AdminUserRow) => <span className="text-sm text-foreground" data-id="element-972" data-mp="3632sl7mym7gx">
          {row.organization_name || '—'}
        </span>
  }, {
    key: 'created',
    header: 'Alta',
    render: (row: AdminUserRow) => <span className="whitespace-nowrap text-sm text-muted-foreground" data-id="element-973" data-mp="2x9661rpvut0z">
          {formatDate(row.created_at)}
        </span>
  }, {
    key: 'counts',
    header: 'Contenido',
    render: (row: AdminUserRow) => <span className="whitespace-nowrap text-sm text-muted-foreground" data-id="element-974" data-mp="2ncursw6qft3w">
          {row.leads_count} leads · {row.forms_count} form. ·{' '}
          {row.landings_count} landings
        </span>
  }, {
    key: 'status',
    header: 'Estado',
    render: (row: AdminUserRow) => row.is_blocked ? <Tag className="bg-red-50 text-red-700" data-id="element-975" data-mp="2d4yy13bbf909">Bloqueado</Tag> : <Tag className="bg-green-50 text-green-700" data-id="element-976" data-mp="2i5my0ehiv06m">Activo</Tag>
  }, {
    key: 'actions',
    header: '',
    className: 'text-right',
    render: (row: AdminUserRow) => <div className="flex flex-wrap justify-end gap-2" data-id="element-977" data-mp="2n2n3ooagm0x7">
          <Button size="sm" variant="secondary" leftIcon={<RotateCcwIcon className="h-3.5 w-3.5" data-id="element-979" data-mp="1ugbuwqcp8tpb" />} disabled={busyId === row.organization_id} onClick={() => setResetTarget(row)} data-id="element-978" data-mp="1pik09oko6thf">
            Reiniciar
          </Button>
          <Button size="sm" variant="secondary" leftIcon={row.is_blocked ? <UnlockIcon className="h-3.5 w-3.5" data-id="element-981" data-mp="2476roonknbp0" /> : <BanIcon className="h-3.5 w-3.5" data-id="element-982" data-mp="24886933sqgbh" />} disabled={busyId === row.user_id} onClick={() => handleToggleBlock(row)} data-id="element-980" data-mp="1ze3tg37une2h">
            {row.is_blocked ? 'Reactivar' : 'Bloquear'}
          </Button>
          <Button size="sm" variant="destructive" leftIcon={<Trash2Icon className="h-3.5 w-3.5" data-id="element-984" data-mp="2pviy4xfzfgzn" />} disabled={busyId === row.organization_id} onClick={() => {
        setDeleteConfirmText('');
        setDeleteTarget(row);
      }} data-id="element-983" data-mp="1bnq1ecp9lf7w">
            Eliminar
          </Button>
        </div>
  }];
  return <div className="space-y-4" data-id="element-985" data-mp="2zvs0kt0khynh">
      <div className="flex items-start gap-3 rounded-lg border border-border bg-surface p-4" data-id="element-986" data-mp="2uvds59v20mox">
        <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" data-id="element-987" data-mp="38v583g54sd5k" />
        <p className="text-sm text-muted-foreground" data-id="element-988" data-mp="38t2aob1nfhqt">
          Panel reservado al super administrador de la plataforma. Las acciones
          afectan a organizaciones de clientes reales y no se pueden deshacer.
        </p>
      </div>

      <Tabs defaultValue="usuarios" data-id="element-989" data-mp="3dld9faknkxhs">
        <TabsList data-id="element-990" data-mp="3dn6kr367o5w4">
          <TabsTrigger value="usuarios" data-id="element-991" data-mp="3dnyes9k5tvka">Usuarios</TabsTrigger>
          <TabsTrigger value="actualizaciones" data-id="element-992" data-mp="3sk7c35khpyu2">Actualizaciones</TabsTrigger>
        </TabsList>

        <TabsContent value="usuarios" data-id="element-993" data-mp="3nkug9vbn5hfc">
          <div className="space-y-3 rounded-lg border border-border bg-background p-5" data-id="element-994" data-mp="3nhyf7y4hycmo">
            <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-995" data-mp="b18go6kg13x1">
              <p className="text-sm text-muted-foreground" data-id="element-996" data-mp="f0r1z3g6fh0e">
                {loading ? 'Cargando organizaciones…' : `${rows.length} usuarios en la plataforma`}
              </p>
              <Button size="sm" variant="ghost" leftIcon={<RefreshCwIcon className="h-4 w-4" data-id="element-998" data-mp="5zj58ptb6o8f" />} onClick={load} loading={loading} data-id="element-997" data-mp="60krzrjosz5z">
                Actualizar lista
              </Button>
            </div>

            {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-id="element-999" data-mp="3s9lxrremmu1k">
                {error}
              </p>}

            <div className="overflow-x-auto" data-id="element-1000" data-mp="3ej0g260wsq59">
              <Table columns={columns} data={rows} rowKey={row => `${row.user_id}-${row.organization_id}`} emptyMessage={loading ? 'Cargando…' : 'No hay organizaciones registradas.'} data-id="element-1001" data-mp="3jfqu6nt5lbo1" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="actualizaciones" data-id="element-1002" data-mp="39hktg9j32bkr">
          <div className="space-y-4 rounded-lg border border-border bg-background p-5" data-id="element-1003" data-mp="29whx08lwo5we">
            <div data-id="element-1004" data-mp="29yuigvqvgye7">
              <div className="flex flex-wrap items-center gap-2" data-id="element-1005" data-mp="2evb5tnq8z8q8">
                <h3 className="text-sm font-semibold text-foreground" data-id="element-1006" data-mp="2eti14dz72ykv">
                  Actualizar sistema
                </h3>
                <span className="rounded-full bg-surface px-2.5 py-1 text-[11px] font-medium text-muted-foreground" data-id="element-1007" data-mp="24x55vbo04co0">
                  Sistema v{SYSTEM_VERSION} · Plantilla {AVA_TEMPLATE_KEY} v
                  {AVA_TEMPLATE_VERSION}
                </span>
              </div>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground" data-id="element-1008" data-mp="2xnwyy09rsl74">
                Revisa todas las organizaciones y completa el formulario
                predeterminado y las landings con lo que falte de la plantilla
                oficial. No borra ni sobrescribe contenido personalizado: logo,
                colores, textos, vídeos, formularios propios, leads y notas se
                mantienen intactos.
              </p>
            </div>

            {syncing && <div className="space-y-2" data-id="element-1009" data-mp="14w0fy3c2q70a">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface" data-id="element-1010" data-mp="zyi6mbu59prr">
                  <div className="h-full rounded-full bg-primary transition-all" style={{
                width: `${syncProgress.total ? syncProgress.done / syncProgress.total * 100 : 0}%`
              }} data-id="element-1011" data-mp="zwnpz9pwojth" />
                </div>
                <p className="text-sm text-muted-foreground" data-id="element-1012" data-mp="ly7e74d6s34h">
                  Actualizando {syncProgress.done} de {syncProgress.total}…
                </p>
              </div>}

            {syncSummary && !syncing && <div className="rounded-lg bg-surface p-4 text-sm text-muted-foreground" data-id="element-1013" data-mp="uznl324sln3y">
                <p className="font-medium text-foreground" data-id="element-1014" data-mp="qxag4fxvdt88">
                  Resumen de la actualización
                </p>
                <p className="mt-1" data-id="element-1015" data-mp="26d9a8nanjoph">
                  {syncSummary.total} organizaciones revisadas ·{' '}
                  {syncSummary.updated} actualizadas · {syncSummary.failed}{' '}
                  con errores
                </p>
              </div>}

            <Button leftIcon={<RefreshCwIcon className="h-4 w-4" data-id="element-1017" data-mp="140mr95cqn60o" />} onClick={() => setSyncOpen(true)} loading={syncing} disabled={rows.length === 0} data-id="element-1016" data-mp="14163c5fpj06s">
              Actualizar sistema
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Reiniciar organización */}
      <ConfirmDialog open={Boolean(resetTarget)} title="Reiniciar organización" description={resetTarget ? `Se borrarán los leads, notas, formularios y landings de ${resetTarget.organization_name ?? 'esta organización'} y se volverán a crear el formulario y las landings por defecto. El usuario y sus credenciales no se tocan.` : ''} confirmLabel="Reiniciar" loading={busyId === resetTarget?.organization_id} onCancel={() => setResetTarget(null)} onConfirm={() => resetTarget && handleReset(resetTarget)} data-id="element-1018" data-mp="gbk2zenlmex7" />

      {/* Actualización masiva */}
      <ConfirmDialog open={syncOpen} title="Actualizar todas las organizaciones" description={`Se revisarán ${rows.length} organizaciones y se completará su contenido con la plantilla oficial. Es una operación segura: no borra ni sobrescribe datos personalizados.`} confirmLabel="Actualizar" onCancel={() => setSyncOpen(false)} onConfirm={handleSyncAll} data-id="element-1019" data-mp="1lma4leq511o" />

      {/* Eliminación definitiva con confirmación escrita */}
      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Eliminar organización y usuario" footer={<div className="flex justify-end gap-2" data-id="element-1021" data-mp="37brhz6tj1lwk">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} data-id="element-1022" data-mp="32hx10b5b3l16">
              Cancelar
            </Button>
            <Button variant="destructive" disabled={deleteConfirmText.trim() !== 'ELIMINAR'} loading={busyId === deleteTarget?.organization_id} onClick={() => deleteTarget && handleDelete(deleteTarget)} data-id="element-1023" data-mp="2xkepcczwmrph">
              Eliminar definitivamente
            </Button>
          </div>} data-id="element-1020" data-mp="3c89hnsa02u21">
        {deleteTarget && <div className="space-y-4" data-id="element-1024" data-mp="2jlezj20no6rq">
            <div className="flex items-start gap-3 rounded-lg bg-red-50 p-4" data-id="element-1025" data-mp="2jmgehhksldeq">
              <AlertTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" data-id="element-1026" data-mp="nhr130pfomig" />
              <p className="text-sm text-red-700" data-id="element-1027" data-mp="sfivc1djwkpt">
                Esta acción elimina la organización en cascada y no se puede
                deshacer. El usuario solo se borrará si no pertenece a otra
                organización.
              </p>
            </div>

            <dl className="space-y-2 rounded-lg bg-surface p-4 text-sm" data-id="element-1028" data-mp="4q80t1msnmk4">
              <div className="flex justify-between gap-4" data-id="element-1029" data-mp="8s2ylw5inht5">
                <dt className="text-muted-foreground" data-id="element-1030" data-mp="8tmf9nv33uvg">Organización</dt>
                <dd className="text-right font-medium text-foreground" data-id="element-1031" data-mp="3qyps5uralb6f">
                  {deleteTarget.organization_name ?? '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-1032" data-mp="1p6ksfx3jx9l5">
                <dt className="text-muted-foreground" data-id="element-1033" data-mp="1p749lbldnty4">Email</dt>
                <dd className="text-right font-medium text-foreground" data-id="element-1034" data-mp="1u5nv5aw273py">
                  {deleteTarget.email ?? '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-1035" data-mp="3el3u9d2gdayr">
                <dt className="text-muted-foreground" data-id="element-1036" data-mp="39pont66ryhcd">Se perderán</dt>
                <dd className="text-right font-medium text-foreground" data-id="element-1037" data-mp="3odroc021t0ts">
                  {deleteTarget.leads_count} leads ·{' '}
                  {deleteTarget.forms_count} formularios ·{' '}
                  {deleteTarget.landings_count} landings
                </dd>
              </div>
            </dl>

            <Input id="admin-delete-confirm" label="Escribe ELIMINAR para confirmar" placeholder="ELIMINAR" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} data-id="element-1038" data-mp="3sfmupiyu64ef" />
          </div>}
      </Modal>
    </div>;
}